<template>
  <liste-template>
    <template #header>
      <h1 class="title is-size-3">{{ $t('listeFactureAchatFournisseur.title') }}</h1>
      <breadcrumb :breadcrumb="breadcrumb"/>
    </template>
    <template #action>
      <button
        class="button is-addOrEdit"
        @click="checkPermissions(perm.maj)
          ? $router.push({ name: 'm.achat.factures' })
          : showPopUp()"
        :class="{ 'button-access-no-permission' : !checkPermissions(perm.maj) }">
        {{ $t('listeFactureAchatFournisseur.actions.add')}}
      </button>
    </template>
    <template #bar-left>
      <form @submit.prevent="recherche()" class="column is-6">
        <b-input
          v-model="critereRecherche.auto"
          :placeholder="$t('general.button.search')"
          type="search"
          icon="magnify">
        </b-input>
      </form>
      <div class="column is-narrow">
        <button class="button" @click="setShowFiltre">
          <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
          <span class="is-size-7">
            {{$t('general.button.filtre')}}
            <label class="has-text-weight-bold" :class="nombreCritere > 0 ? '' : 'is-invisible'">
              ({{ nombreCritere }})
            </label>
          </span>
        </button>
        <span
          @click="reinitialiserFiltre()"
          class="info-circle-right is-pulled-right is-cursor-pointer">
          <b-icon icon="close-circle" type="is-danger"></b-icon>
        </span>
      </div>
    </template>
    <template #bar-right>
      <div class="column is-narrow">
        <exportExcelButton
          :perm="perm.exporter"
          inProgress/>
      </div>
      <parametrage-table
        :reference-table="referenceTable"
        @endLoadParamtre="startArrayRendering = true">
      </parametrage-table>
    </template>
    <template #content>
      <v-server-table
        v-if="startArrayRendering" url=""
        :columns="columns"
        :options="options"
        :ref="referenceTable"
        @loaded="finLoading"
        @loading="debutLoading">
        <template #code="{ row }">
          <router-link
            :to="{ name: 'c.achat.factures', params: { id: row.id }}"
            :class="{'text-access-no-permission': !checkPermissions(perm.lecture)}"
            class="lien">
            {{ row.code }}
          </router-link>
        </template>
        <template #dateDevisCommande="{ row }">
          {{ func.formatDate(row.dateDevisCommande) }}
        </template>
        <template #fournisseur="{ row }">
          <router-link-blank
            v-for="(inter, index) in row.intervenants"
            :key="index"
            :to="getRouteToTiers(inter.tiers)"
            :message="inter.nomCommercial">
          </router-link-blank>
        </template>
        <template #sousType="{ row }">
          {{ getSousTypeLibelle(row.sousType) }}
        </template>
        <template #etat="{ row }">
          {{ getEtatLibelle(row.etat) }}
        </template>
        <template #montantHt="{ row }">
          {{ func.formatDevise(row.montantHt) }}
        </template>
        <template #montantTtc="{ row }">
          {{ func.formatDevise(row.montantTtc) }}
        </template>
        <template #montantRegle="{ row }">
          {{ func.formatDevise(row.montantRegle) }}
        </template>
        <template #edit="{ row }">
          <div class="has-text-centered">
            <btn-action-icon
              :permission="perm.maj"
              @is-click="$router.push({ name: 'm.achat.factures', params: { id: row.id } })"
              icon-type="is-success"
              icon="pencil-box-outline">
            </btn-action-icon>
          </div>
        </template>
        <template #archiver>
          <div class="has-text-centered">
            <btn-action-icon
              :permission="perm.supprimer"
              @is-click="inProgress()"
              icon-type="is-default"
              icon="archive">
            </btn-action-icon>
          </div>
        </template>
        <template #delete>
          <div class="has-text-centered">
            <btn-action-icon
              :permission="perm.supprimer"
              @is-click="inProgress()"
              icon-type="is-danger"
              icon="delete-forever">
            </btn-action-icon>
          </div>
        </template>
        <template #afterTable>
          <table-items-in-view
            :perPage="resultPerPage"
            @input="$refs[referenceTable].setLimit($event)"/>
        </template>
      </v-server-table>
    </template>
    <template #filter>
      <div v-show="showFiltre" class="componentBrief">
        <filtre-liste
          :critere-recherche="critereRecherche"
          @toggleFiltre="setShowFiltre"
          @recherche="makeRechercheFilter"
          ref="filtreListe"/>
      </div>
    </template>
  </liste-template>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import Breadcrumb from '@/components/general/breadcrumb';
import BtnActionIcon from '@/components/general/btnActionIcon';
import ParametrageTable from '@/components/general/parametrageTable';
import TableItemsInView from '@/components/general/TableItemsInView';
import ListeTemplate from '@/components/general/template/listeTemplate';
import FiltreListe from '@/components/achat/facture/filtreFactureAchatFournisseur';
import ExportExcelButton from '@/components/general/button/exportExcelButton';
import RouterLinkBlank from '@/components/general/routerLinkBlank';
import inProgress from '@/services/general/inProgress';
import messageErreur from '@/services/general/messageErreur';
import subscribeMutation from '@/services/store/subscribeMutation';
import formatDate from '@/services/general/formatDate';
import { formatDevise } from '@/services/general/filters/formatDevise';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import checkPeriodeAndFormat from '@/services/dcf/checkPeriodeAndFormat';
import tagInputToArray from '@/services/general/filters/tagInputToArray';
import * as tableConfig from '@/configuration/clientTableConf';
import { FACTURES } from '@/configuration/achat/permissionsConf';
import { TIERS_TYPE_FOURNISSEUR } from '@/configuration/tiers/tiersConf';
import { REFERENCE_TABLE_LISTE_FACTURES_ACHAT_FOURNISSEUR } from '@/configuration/general/referenceTableConf';
import { FACTURE_ETAT, FACTURE_SOUS_TYPE } from '@/configuration/facture/factureConf';
import { SET_DETECT_CHANGE_SOCIETE, SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import { LISTE_INCLUSION_DCF } from '@/configuration/dcf/dcfConf';

const Societe = createNamespacedHelpers('societe');
const Permissions = createNamespacedHelpers('permissions');
const FacturesAchatsFournisseur = createNamespacedHelpers('facturesAchatsFournisseur');

export default {
  name: 'LFactureAchatFournisseur',
  data() {
    return {
      perm: FACTURES,
      showFiltre: false,
      unsubscribeValue: null,
      startArrayRendering: false,
      resultPerPage: tableConfig.RESULTS_PER_PAGE,
      referenceTable: REFERENCE_TABLE_LISTE_FACTURES_ACHAT_FOURNISSEUR,
      func: { formatDate, formatDevise },
    };
  },
  components: {
    Breadcrumb,
    FiltreListe,
    ListeTemplate,
    BtnActionIcon,
    ParametrageTable,
    TableItemsInView,
    ExportExcelButton,
    RouterLinkBlank,
  },
  computed: {
    ...FacturesAchatsFournisseur.mapGetters({
      items: 'items',
      totalCount: 'total',
      critereRecherche: 'criteresRecherche',
    }),
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Societe.mapGetters({
      lockMenu: 'lockMenu',
      societeActiveId: 'societeActiveId',
      agencesSelectionnees: 'agenceActive',
    }),
    breadcrumb() {
      const output = [
        { title: this.$i18n.t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('listeFactureAchat.breadcrumb.liste'), route: '', isActive: true },
      ];
      return output;
    },
    nombreCritere() {
      const banCritereKey = ['auto', 'dates', 'module', 'sens'];
      let nombreCritere = calculNombreCritere(this.critereRecherche, banCritereKey);
      const { dates } = this.critereRecherche;
      if (dates.dateDebut || dates.dateFin) nombreCritere += 1;
      return nombreCritere <= 9 ? nombreCritere : '+9';
    },
    columnsData() {
      return [
        { name: 'code', sortable: true },
        { name: 'dateDevisCommande', sortable: true },
        { name: 'fournisseur', sortable: false },
        { name: 'sousType', sortable: true },
        { name: 'etat', sortable: true },
        { name: 'montantHt', sortable: true },
        { name: 'montantTtc', sortable: true },
        { name: 'montantRegle', sortable: false },
        { name: 'edit', noTranslate: true },
        { name: 'archiver', noTranslate: true },
        { name: 'delete', noTranslate: true },
      ];
    },
    columns() {
      return this.columnsData.map((column) => column.name);
    },
    options() {
      const headings = this.columnsData.reduce((acc, val) => {
        acc[val.name] = !val.noTranslate ? this.$t(`listeFactureAchatFournisseur.table.${val.name}`) : '';
        return acc;
      }, {});
      return {
        headings,
        sortIcon: tableConfig.SORT_ICON,
        filterable: false,
        perPageValues: tableConfig.NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: tableConfig.TEXTS,
        sortable: this.columnsData.filter((c) => c.sortable).map((c) => c.name),
        requestFunction: ((data) => {
          data.query = cloneDeep(this.critereRecherche);
          checkPeriodeAndFormat(data.query, 'dates');
          data.query.tiersId = tagInputToArray(data.query.tiersId, 'id');
          data.query.etat = tagInputToArray(data.query.etat, 'code');
          if (!data.query.agencesId.length) {
            data.query.agencesId = tagInputToArray(this.agencesSelectionnees, 'agenceId');
          }
          data.query.societeId = this.societeActiveId;
          data.query.aRapprocher = true;
          data.inclusion = LISTE_INCLUSION_DCF.map(el => el.value);
          return this.actionLoad(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.items,
            count: this.totalCount,
          };
        }),
        columnsClasses: {
          edit: 'colonneSelected',
          delete: 'colonneSelected',
          archiver: 'colonneSelected',
        },
      };
    },
  },
  methods: {
    ...FacturesAchatsFournisseur.mapActions({
      actionLoad: 'loadItems',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    getRouteToTiers(tiers) {
      const routeName = tiers.type === TIERS_TYPE_FOURNISSEUR
        ? 'c.achat.fournisseurs'
        : 'c.bodac.clients/contacts-client';
      return { name: routeName, params: { id: tiers.id } };
    },
    getEtatLibelle(etat) {
      const find = FACTURE_ETAT.find((el) => el.code === etat);
      return find ? this.$t(find.libelle) : '';
    },
    getSousTypeLibelle(code) {
      const find = FACTURE_SOUS_TYPE.find((el) => el.code === code);
      return find ? this.$t(find.libelle) : '';
    },
    setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) this.$refs.filtreListe.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    reinitialiserFiltre() {
      this.$refs.filtreListe.initializeFiltre();
      this.$refs.filtreListe.viderCritere();
      this.$refs.filtreListe.appliquerCritere();
      this.showFiltre = false;
    },
    finLoading() {
      this.loadingComponent.close();
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    recherche() {
      this.$refs[this.referenceTable].refresh();
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.setShowFiltre();
      this.recherche();
    },
    inProgress() {
      inProgress(this.$i18n);
    },
    goFirstPage() {
      this.$refs[this.referenceTable].setPage(1);
    },
  },
  beforeDestroy() {
    this.unsubscribeValue();
    if (this.lockMenu) this.setLockMenu(false);
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.recherche, this.goFirstPage);
      this.setInformationPage(this.$t('pageAchat.factures.liste'));
    } catch (e) {
      messageErreur(this.$i18n);
      throw (e);
    } finally {
      loadingComponent.close();
    }
  },
};
</script>
